<template>
    <div
        class="mt-10 overflow-hidden rounded-xl border border-gray-200 bg-white"
    >
        <div class="border-b border-gray-100 px-6 py-5">
            <h3 class="font-medium text-gray-900">
                {{ $t("vue.domain_overview") }}
            </h3>
            <p class="mt-1 text-sm text-gray-600">
                Monitor DMARC compliance across all your domains
            </p>
        </div>
        <div class="overflow-x-auto">
            <table class="w-full">
                <thead class="border-b border-gray-100 bg-gray-50">
                    <tr>
                        <th
                            class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                        >
                            {{ $t("vue.dashboard_table_domain") }}
                        </th>
                        <th
                            class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                        >
                            {{ $t("vue.dashboard_table_emails_reported") }}
                        </th>
                        <th
                            class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                        >
                            {{ $t("vue.dashboard_table_dmarc_compliance") }}
                        </th>
                        <th
                            class="px-6 py-4 text-left text-right text-sm font-medium text-gray-600"
                        >
                            {{ $t("vue.dashboard_table_current_policy") }}
                        </th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <template
                        v-for="(domainList, group) in {
                            active: activeDomains,
                            inactive: inactiveDomains,
                            notExisting: notExistingDomains,
                        }"
                    >
                        <tr
                            v-for="(domain, index) in domainList"
                            :key="domain"
                            class="transition-colors duration-150 hover:bg-gray-50"
                        >
                            <td class="px-6 py-4">
                                <div class="flex items-center gap-3">
                                    <div
                                        v-if="group === 'active'"
                                        class="h-2 w-2 rounded-full bg-green-500"
                                    ></div>
                                    <div
                                        v-else
                                        class="h-2 w-2 rounded-full bg-gray-400"
                                    ></div>
                                    <a
                                        :href="
                                            route('reports.show', {
                                                domain: domain.domain,
                                            })
                                        "
                                        class="group/domain -m-2 flex cursor-pointer items-center gap-3 rounded-lg p-2 text-left font-medium transition-colors duration-150 hover:bg-blue-50"
                                        ><span
                                            class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 group-hover/domain:underline"
                                            >{{ domain.domain }}</span
                                        ></a
                                    >
                                    <span
                                        v-if="group === 'inactive'"
                                        data-slot="badge"
                                        class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                        >inactive</span
                                    >
                                    <span
                                        v-if="group === 'notExisting'"
                                        data-slot="badge"
                                        class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                        >not existing</span
                                    >
                                </div>
                            </td>
                            <template
                                v-if="
                                    domain.report_current.pass > 0 ||
                                    domain.report_current.fail > 0
                                "
                            >
                                <td class="px-6 py-4">
                                    <span class="text-gray-900">{{
                                        formatNumber(
                                            domain.report_current.fail +
                                                domain.report_current.pass,
                                        )
                                    }}</span>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="flex items-center gap-3">
                                        <div class="max-w-24 flex-1">
                                            <div
                                                class="h-2 w-full rounded-full bg-[#F97171]"
                                            >
                                                <div
                                                    class="h-2 rounded-full bg-[#11B981] transition-all duration-300"
                                                    :style="{
                                                        width: calculateCompliance(
                                                            domain
                                                                .report_current
                                                                .pass,
                                                            domain
                                                                .report_current
                                                                .fail,
                                                        ),
                                                        color: calculateColorByPercents(
                                                            calculateCompliance(
                                                                domain
                                                                    .report_current
                                                                    .pass,
                                                                domain
                                                                    .report_current
                                                                    .fail,
                                                            ),
                                                        ),
                                                    }"
                                                ></div>
                                            </div>
                                        </div>
                                        <span
                                            class="text-sm font-medium text-green-600"
                                            >{{
                                                calculateCompliance(
                                                    domain.report_current.pass,
                                                    domain.report_current.fail,
                                                )
                                            }}</span
                                        >
                                    </div>
                                </td>
                                <td class="px-6 py-4 text-right">
                                    <span
                                        v-if="domain.dmarcPolicy === 'reject'"
                                        data-slot="badge"
                                        class="w-fit focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&]:hover:bg-primary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-green-200 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 transition-[color,box-shadow] hover:bg-green-100 focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3"
                                        >Reject</span
                                    >
                                    <span
                                        v-else-if="
                                            domain.dmarcPolicy === 'quarantine'
                                        "
                                        data-slot="badge"
                                        class="w-fit focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&]:hover:bg-primary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 transition-[color,box-shadow] hover:bg-blue-100 focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3"
                                        >Quarantine</span
                                    >
                                    <span
                                        v-else
                                        data-slot="badge"
                                        class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                        >None</span
                                    >
                                </td>
                            </template>
                            <template v-else>
                                <td colspan="3" class="px-6 py-4">
                                    <div
                                        v-if="group === 'active'"
                                        class="flex items-center justify-center gap-2 py-2 text-gray-500"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-clock h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <polyline
                                                points="12 6 12 12 16 14"
                                            ></polyline></svg
                                        ><span class="text-sm"
                                            >Waiting for reports</span
                                        >
                                    </div>
                                </td>
                            </template>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>
<script setup lang="ts">
import route from "ziggy-js";
import useNumberFormat from "@/composables/useNumberFormat";

defineProps<{
    activeDomains: any[];
    inactiveDomains: any[];
    notExistingDomains: any[];
}>();

const { formatNumber } = useNumberFormat();

const calculateCompliance = (pass, fail) => {
    return pass + fail > 0
        ? Math.floor((pass / (pass + fail)) * 100) + "%"
        : 0 + "%";
};

const calculateColorByPercents = (percents) => {
    var percentNum = parseInt(percents.replace("%", ""));

    if (percentNum > 75) {
        return "#00c951";
    }

    if (percentNum > 50) {
        return "#f0b100";
    }

    return "#fb2c36";
};
</script>
